// src/components/excelDownload/excelDownloadButton.js
import React, { useState } from "react";
import { FaFileExcel, FaCheckCircle } from "react-icons/fa";
import * as XLSX from "xlsx";
import Notification from "../customNotification/Notification";
import useNotification from "../../hooks/useNotification";
import "./excelDownloadButton.css";

const ExcelDownloadButton = ({ data = [], filename = "data-export", sheetName = "Sheet1", variant = "outline", size = "small", disabled = false, className = "", children }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const { notification, showNotification, hideNotification } = useNotification();

  const handleDownload = async () => {
    if (!data || data.length === 0) {
      alert("No data available to download");
      return;
    }

    setIsDownloading(true);

    try {
      // Create a new workbook
      const wb = XLSX.utils.book_new();

      // Create worksheet from data
      const ws = XLSX.utils.json_to_sheet(data);

      // Auto-size columns based on content
      const colWidths = [];
      if (data.length > 0) {
        Object.keys(data[0]).forEach((key, index) => {
          const maxLength = Math.max(key.length, ...data.map((row) => String(row[key] || "").length));
          colWidths[index] = { wch: Math.min(Math.max(maxLength, 10), 50) };
        });
        ws["!cols"] = colWidths;
      }

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split("T")[0];
      const finalFilename = `${filename}-${timestamp}.xlsx`;

      // Download the file
      XLSX.writeFile(wb, finalFilename);

      // Show success state
      setDownloadSuccess(true);
      setTimeout(() => setDownloadSuccess(false), 2000);
      showNotification("Excel downloaded successfully", "success");
    } catch (error) {
      console.error("Error generating Excel file:", error);
      showNotification("Error generating Excel file. Please try again.", "error");
    } finally {
      setIsDownloading(false);
    }
  };

  // Button variants with pop-up style
  const variants = {
    primary: "excel-btn-primary",
    secondary: "excel-btn-secondary",
    outline: "excel-btn-outline",
    ghost: "excel-btn-ghost"
  };

  // Button sizes
  const sizes = {
    small: "excel-btn-small",
    medium: "excel-btn-medium",
    large: "excel-btn-large"
  };

  const buttonClasses = `
    excel-download-btn
    ${variants[variant]}
    ${sizes[size]}
    ${className}
    ${isDownloading ? "excel-btn-loading" : ""}
    ${downloadSuccess ? "excel-btn-success" : ""}
  `.trim();

  return (
    <>
      <Notification message={notification.message} type={notification.type} show={notification.show} onClose={hideNotification} duration={4000} />

      <button onClick={handleDownload} disabled={disabled || isDownloading} className={buttonClasses}>
        {isDownloading ? (
          <>
            <div className="excel-btn-spinner" />
            Exporting...
          </>
        ) : downloadSuccess ? (
          <>
            <FaCheckCircle />
            Success!
          </>
        ) : (
          <>
            <FaFileExcel />
            {children || "Download"}
          </>
        )}
      </button>
    </>
  );
};

export default ExcelDownloadButton;
